import type {
  Project,
  Diagram,
  CreateProjectInput,
  UpdateProjectInput,
  UpdateDiagramInput,
  AnalysisJob,
  EffectivePattern,
  DetectionPatternCategory,
  TestPatternResult,
  UpdatePatternOverrideInput,
  SavedGraphView,
  GraphFilterState,
  StructuralSummaryPayload,
  OperationalSummaryPayload,
  SecuritySummaryPayload,
} from '@veodiagram/shared';
import { useAuthStore } from '../stores/authStore';

// Issue #818: Stitch review response shapes. Exported so StitchReviewPage
// and its sub-components can share the same types instead of redeclaring.
export interface StitchCallSiteKey {
  filePath: string;
  enclosingSymbol: string;
  urlPattern: string;
  method: string;
  wrapperChainHash: string;
}
export interface StitchRouteKey {
  framework: string;
  method: string;
  path: string;
  handlerSymbol: string;
  filePath: string;
}
export interface StitchApiCallSite {
  callSiteId: string;
  callSiteKey: StitchCallSiteKey;
  filePath: string;
  lineStart: number;
  lineEnd: number;
  urlPattern: string;
  method: string;
  library: string;
  wrapperChain: string[];
  wrapperFunction?: string;
  isDynamicEndpoint: boolean;
}
export interface StitchRouteEndpoint {
  routeId: string;
  routeKey: StitchRouteKey;
  method: string;
  path: string;
  framework: string;
  handlerFunction?: string;
}
export interface StitchCandidateExplanation {
  route: StitchRouteEndpoint;
  score: number;
  breakdown: {
    methodMatch: boolean;
    pathOverlap: number;
    pathLengthDiff: number;
    suffixMatch: boolean;
    exactMatch: boolean;
  };
  rationale: string;
}
export interface StitchAmbiguousCandidateGroup {
  site: StitchApiCallSite;
  candidates: StitchCandidateExplanation[];
  tier: 'exact' | 'pathSuffix';
  rationale: string;
}
export interface StitchUnresolvedCandidateGroup {
  site: StitchApiCallSite;
  candidates: StitchCandidateExplanation[];
}
export interface StitchEntry {
  callSite: StitchCallSiteKey;
  route: StitchRouteKey;
  confidence: number;
  source: 'auto' | 'user' | 'ai';
  rationale: string;
  createdAt: string;
  /** Chain-of-thought reasoning from the AI (reasoning models only). */
  aiReasoning?: string;
  /** Who created this stitch (user ID for user/ai sources). */
  createdBy?: string;
}
export interface StitchRuleEntry {
  id: string;
  match: { kind: string; pattern: string };
  target: { kind: string; pattern: string; method?: string };
  confidence: number;
  note?: string;
}
/** Issue #885: reason code for the unresolvable bucket. */
export type UnresolvableReason = 'dynamic' | 'traced-via-call-graph' | 'env-var';

export interface StitchUnresolvableCandidateGroup {
  site: StitchApiCallSite;
  reason: UnresolvableReason;
}

export interface StitchReviewResponse {
  state: 'ready' | 'no-analysis' | 'no-call-graphs';
  unresolved: StitchApiCallSite[];
  ambiguous: StitchEntry[];
  ambiguousCandidates: StitchAmbiguousCandidateGroup[];
  unresolvedCandidates: StitchUnresolvedCandidateGroup[];
  /** Issue #885: call sites the tracer couldn't resolve — optional
   *  for back-compat with older servers. */
  unresolvable?: StitchApiCallSite[];
  unresolvableCandidates?: StitchUnresolvableCandidateGroup[];
  stitches: StitchEntry[];
  userStitches: StitchEntry[];
  /** Issue #827: all confident stitches merged with provenance. */
  allStitches: StitchEntry[];
  rules: StitchRuleEntry[];
  ignored: StitchCallSiteKey[];
}
export interface StitchPromptPreview {
  model: string;
  systemPrompt: string;
  userMessage: string;
  callSite: StitchApiCallSite;
  candidates: StitchRouteEndpoint[];
}
export interface StitchProposeResult {
  ok: boolean;
  proposed: number;
  proposedStitches?: StitchEntry[];
  skipped: number;
  /** Why the AI declined — reason + human-readable detail per skipped site. */
  skipReasons?: Array<{
    callSiteId: string;
    reason: string;
    detail?: string;
  }>;
  capped: number;
  totalTokensUsed?: number;
}

const API_BASE = '/api';

// Handle 401 responses by clearing stale auth state.
// #1080 v1.0.1: when the server returns 401 with code USER_NOT_FOUND, the
// JWT cookie was valid but the userId it encoded no longer exists (DB
// reset / user delete). Redirect explicitly to /login with a session-
// expired reason so the page can show a useful banner. Generic 401
// (expired JWT, missing cookie) keeps the previous behavior of silently
// clearing the auth store — components that need auth will re-prompt.
function handleUnauthorized(body?: { code?: string }): void {
  // Get the store state directly (works outside React components)
  const state = useAuthStore.getState();
  if (state.isAuthenticated) {
    state.logout();
  }
  if (body?.code === 'USER_NOT_FOUND' && typeof window !== 'undefined') {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?reason=session-expired';
    }
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  // Only include Content-Type header when there's a body to send
  const headers: Record<string, string> = {};

  // Copy existing headers if any
  if (options?.headers) {
    const existingHeaders = options.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  // Add Content-Type only when there's a body
  if (options?.body) {
    headers['Content-Type'] = 'application/json';
  }

  // Issue #855: wrap fetch in try/catch to convert network-level TypeError
  // ("Failed to fetch") into a descriptive error message instead of crashing.
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (err) {
    // Network error — server unreachable, CORS failure, etc.
    throw new Error(
      `Cannot connect to server (${endpoint}). Please ensure the API server is running.`,
    );
  }

  if (!response.ok) {
    // Read body once and pass through to the 401 handler so it can branch
    // on the `code` discriminator (#1080 USER_NOT_FOUND).
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    if (response.status === 401) {
      handleUnauthorized(error as { code?: string });
    }
    throw new Error(
      (error as { message?: string }).message || `HTTP error! status: ${response.status}`,
    );
  }

  // Handle 204 No Content responses (e.g., DELETE operations)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// AI Status response type
export interface AIStatus {
  available: boolean;
  /** Issue #824: explicit mode. */
  mode?: 'ai-enhanced' | 'basic';
  provider: string | null;
  model: string | null;
  cacheEnabled: boolean;
  features: {
    systemContext: boolean;
    threatModel: boolean;
    serviceMesh: boolean;
    sreRunbook: boolean;
    /** Issue #824 */
    stitchProposer?: boolean;
    flowEnhancement?: boolean;
    aiNaming?: boolean;
    diagramGeneration?: boolean;
  };
  /** Issue #824: health check fields. */
  healthy?: boolean | null;
  lastCheckedAt?: string | null;
  lastError?: string | null;
  latencyMs?: number | null;
  message: string;
}

// Server capabilities response type
export interface ServerCapabilities {
  localAnalysis: boolean;
  authMode: 'github' | 'local' | 'both';
  aiEnabled: boolean;
}

// Local auth response type
export interface LocalAuthResponse {
  success: boolean;
  token: string;
  recoveryToken?: string; // For session persistence (Issue #164)
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    isLocalUser: boolean;
  };
}

// Recovery response type (Issue #164)
export interface RecoverResponse {
  success: boolean;
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    isLocalUser: boolean;
  };
}

// API Key status response type
export interface ApiKeyStatus {
  hasApiKey: boolean;
  provider: 'anthropic' | 'openai' | 'gemini' | 'openrouter' | 'custom';
  baseUrl: string | null;
  model: string | null;
  systemAiEnabled: boolean;
}

// API Key configuration input
export interface ApiKeyConfig {
  apiKey: string;
  provider: 'anthropic' | 'openai' | 'gemini' | 'openrouter' | 'custom';
  baseUrl?: string | null;
  model?: string | null;
}

// AI Test result
export interface AITestResult {
  success: boolean;
  provider?: string;
  model?: string;
  latencyMs?: number;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    estimatedCost: number;
  };
  error?: string;
  message: string;
}

// AI Models result
export interface AIModelsResult {
  success: boolean;
  provider?: string;
  models?: Array<{ id: string; name: string }>;
  error?: string;
  message?: string;
}

// Agent status types (Issue #157)
export type AgentStatus = 'idle' | 'analyzing' | 'generating' | 'complete' | 'error';

export interface AgentLog {
  timestamp: string;
  message: string;
  level: 'info' | 'debug' | 'warn';
}

export interface AgentRun {
  id: string;
  agentId: string;
  agentName: string;
  diagramType: string;
  status: AgentStatus;
  progress: number;
  currentStep: string | null;
  insights: string | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  logs?: AgentLog[];
}

export interface AgentMetadata {
  id: string;
  name: string;
  description: string;
  diagramType: string;
  category: string;
}

// Detection Pattern types (Issue #381)
export interface CategoryInfo {
  key: DetectionPatternCategory;
  displayName: string;
  description: string;
  patternCount?: number;
}

export interface DetectionPatternsResponse {
  patterns: EffectivePattern[];
  categories: Record<string, EffectivePattern[]>;
  stats: {
    totalPatterns: number;
    customizedCount: number;
    disabledCount: number;
  };
  categoryInfo: CategoryInfo[];
}

export interface PatternUpdateResponse {
  success: boolean;
  override: {
    id: string;
    patternKey: string;
    enabled: boolean;
    customRegex: string | null;
    confidence: number | null;
  };
  effectiveRegex: string;
  effectiveConfidence: number;
}

export interface TestPatternRequest {
  regex: string;
  flags?: string;
  sampleCode: string;
}

// Issue #465: Analysis Results types
export interface FunctionCallInfo {
  caller: string;       // Function making the call
  callee: string;       // Function being called
  line: number;         // Line number of the call
  isAsync: boolean;     // Whether the call is awaited
}

export interface ParsedFileSummary {
  filePath: string;
  imports: Array<{ source: string; specifiers: string[] }>;
  exports: string[];
  functions: Array<{ name: string; isAsync: boolean; line: number }>;
  functionCalls: FunctionCallInfo[];
  errors?: string[];
}

export interface FrontendCaller {
  component: string;
  handler: string;
  apiCallId: string;
  sourceFile: string;
  sourceLine: number;
}

export interface ASTSummary {
  totalFiles: number;
  parsedCount: number;
  errorCount: number;
  totalFunctions: number;
  totalImports: number;
  totalExports: number;
}

export interface AnalysisResultsResponse {
  id: string;
  projectId: string;
  astSummary: ASTSummary;
  parsedFiles: ParsedFileSummary[];
  apiEndpoints: unknown[]; // ApiRoute[] from shared types
  apiCalls: unknown[]; // ApiCall[] from shared types
  frontendCallers: FrontendCaller[];
  interactions: unknown[]; // UserInteraction[] from shared types
  createdAt: string;
}

// #1045: Generated-diagram types (#466) removed — the corresponding
// backend model + routes were dropped in #666 ("remove Mermaid backend
// code and drop GeneratedDiagram model"). The SPA was still calling
// /api/diagrams/generated/project/:projectId and
// /api/diagrams/generate-from-analysis, producing broken flow stitches
// and 404s in the network tab.

export const api = {
  // Server capabilities (public endpoint, no auth required)
  getCapabilities: () => fetchApi<ServerCapabilities>('/public/capabilities'),

  // AI Status (public endpoint, no auth required)
  getAIStatus: () => fetchApi<AIStatus>('/ai/status'),

  // Auth
  getCurrentUser: () => fetchApi('/auth/me'),

  // Local/anonymous authentication (for local repos without GitHub)
  loginLocal: () =>
    fetchApi<LocalAuthResponse>('/auth/local', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Recover session using recovery token (Issue #164)
  recoverSession: (recoveryToken: string) =>
    fetchApi<RecoverResponse>('/auth/recover', {
      method: 'POST',
      body: JSON.stringify({ recoveryToken }),
    }),

  // API Key Configuration
  getApiKeyStatus: () => fetchApi<ApiKeyStatus>('/auth/api-key/status'),

  saveApiKey: (config: ApiKeyConfig) =>
    fetchApi<{ success: boolean; message: string }>('/auth/api-key', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),

  deleteApiKey: () =>
    fetchApi<{ success: boolean; message: string }>('/auth/api-key', {
      method: 'DELETE',
    }),

  testAIConnection: () =>
    fetchApi<AITestResult>('/ai/test', {
      method: 'POST',
    }),

  fetchAIModels: (provider: string, apiKey: string) =>
    fetchApi<AIModelsResult>('/ai/models', {
      method: 'POST',
      body: JSON.stringify({ provider, apiKey }),
    }),

  // Projects
  getProjects: () => fetchApi<Project[]>('/projects'),

  getProject: (id: string) => fetchApi<Project>(`/projects/${id}`),

  createProject: (data: CreateProjectInput) =>
    fetchApi<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProject: (id: string, data: UpdateProjectInput) =>
    fetchApi<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteProject: (id: string, permanent = false) =>
    fetchApi<void>(`/projects/${id}${permanent ? '?permanent=true' : ''}`, { method: 'DELETE' }),

  // Project Trash (Issue #159)
  getDeletedProjects: () => fetchApi<Project[]>('/projects/deleted'),

  restoreProject: (id: string) =>
    fetchApi<Project>(`/projects/${id}/restore`, { method: 'POST' }),

  emptyProjectTrash: () =>
    fetchApi<{ deleted: number }>('/projects/trash/empty', { method: 'DELETE' }),

  getProjectDeletePreview: (id: string) =>
    fetchApi<{
      id: string;
      name: string;
      diagramCount: number;
      analysisJobCount: number;
      sampleDiagrams: Array<{ name: string; type: string }>;
    }>(`/projects/${id}/delete-preview`),

  // Analysis Results (Issue #465)
  getAnalysisResults: (projectId: string) =>
    fetchApi<AnalysisResultsResponse>(`/projects/${projectId}/analysis-results`),

  // Analysis
  previewAnalysis: (projectId: string) =>
    fetchApi<{
      totalFiles: number;
      breakdown: Record<string, number>;
      estimatedTime: string;
      excludePatterns: string[];
    }>(`/projects/${projectId}/analyze/preview`, { method: 'POST' }),

  startAnalysis: (projectId: string, diagramTypes?: string[]) =>
    fetchApi<{ jobId: string }>(`/projects/${projectId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ diagramTypes }),
    }),

  // veodiagram-issues#25 — pre-analysis file-scope review.
  discoverFileScope: (projectId: string) =>
    fetchApi<{
      totalFiles: number;
      files: Array<{
        path: string;
        autoIncluded: boolean;
        autoRole: 'frontend' | 'backend' | 'shared' | 'config' | 'test' | 'unknown';
        autoReason: string;
      }>;
    }>(`/projects/${projectId}/file-scope/discover`, { method: 'POST' }),

  getFileScope: (projectId: string) =>
    fetchApi<{
      entries: Array<{
        filePath: string;
        included: boolean;
        role: 'frontend' | 'backend' | 'shared' | 'config' | 'test' | 'unknown';
        source: 'auto' | 'ai' | 'user';
        autoReason: string | null;
      }>;
    }>(`/projects/${projectId}/file-scope`),

  saveFileScope: (
    projectId: string,
    entries: Array<{
      path: string;
      included: boolean;
      role: 'frontend' | 'backend' | 'shared' | 'config' | 'test' | 'unknown';
    }>,
  ) =>
    fetchApi<{ written: number }>(`/projects/${projectId}/file-scope`, {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    }),

  resetFileScope: (projectId: string) =>
    fetchApi<{ cleared: true }>(`/projects/${projectId}/file-scope`, {
      method: 'DELETE',
    }),

  // veodiagram-issues#40 — route/URL user overrides
  listRouteUrlOverrides: (projectId: string) =>
    fetchApi<{
      entries: Array<{
        id: string;
        filePath: string;
        line: number;
        kind: 'route' | 'apiCallUrl' | 'externalHttpUrl';
        autoValue: string | null;
        userValue: string;
        reason: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
    }>(`/projects/${projectId}/route-url-overrides`),

  // veodiagram-issues#41 — list unresolved analyzer entries (those
  // the review modal should prompt the user about).
  listUnresolvedRouteUrls: (projectId: string) =>
    fetchApi<{
      count: number;
      entries: Array<{
        filePath: string;
        line: number;
        kind: 'route' | 'apiCallUrl' | 'externalHttpUrl';
        autoValue: string;
        existingOverride: {
          userValue: string;
          autoValue: string | null;
          reason: string | null;
        } | null;
        drifted: boolean;
      }>;
    }>(`/projects/${projectId}/route-url-overrides/unresolved`),

  saveRouteUrlOverrides: (
    projectId: string,
    entries: Array<{
      filePath: string;
      line: number;
      kind: 'route' | 'apiCallUrl' | 'externalHttpUrl';
      autoValue?: string | null;
      userValue: string;
      reason?: string | null;
    }>,
  ) =>
    fetchApi<{ written: number }>(`/projects/${projectId}/route-url-overrides`, {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    }),

  deleteRouteUrlOverride: (
    projectId: string,
    args?: { filePath: string; line: number; kind: 'route' | 'apiCallUrl' | 'externalHttpUrl' },
  ) => {
    const query = args
      ? `?filePath=${encodeURIComponent(args.filePath)}&line=${args.line}&kind=${args.kind}`
      : '';
    return fetchApi<{ cleared: true }>(`/projects/${projectId}/route-url-overrides${query}`, {
      method: 'DELETE',
    });
  },

  getAnalysisStatus: (jobId: string) =>
    fetchApi<AnalysisJob>(`/analysis/${jobId}`),

  // veodiagram-issues#15 — screen-name registry + refine
  /** List the per-project screen-name override registry (user / AI /
   * cluster overrides). The catalog consults this at priority 0 in
   * `inferScreenFromPath` so labels survive across refines.
   *
   * veodiagram-issues#19: each row carries `routePath` — empty
   * string means file-level fallback; non-empty means a specific
   * route (e.g. "/views/auth/signin"). Multiple rows per filePath
   * are normal for server-rendered handler files that route many
   * screens. */
  getScreenOverrides: (projectId: string) =>
    fetchApi<{
      overrides: Array<{
        filePath: string;
        routePath: string;
        name: string;
        category: string | null;
        source: 'ai' | 'user' | 'cluster';
        confidence: number | null;
        modelId: string | null;
        promptVersion: string | null;
        updatedAt: string;
      }>;
    }>(`/projects/${projectId}/screen-overrides`),

  /** Kick off a screen-name refine job. Returns {jobId}; poll
   * `getAnalysisStatus(jobId)` for progress. */
  refineScreenNamesWithAI: (projectId: string, filePaths: string[]) =>
    fetchApi<{ jobId: string; selectedCount: number }>(
      `/projects/${projectId}/screen-names/refine-with-ai`,
      { method: 'POST', body: JSON.stringify({ filePaths }) },
    ),

  // Agent Status (Issue #157)
  getAgentRuns: (jobId: string) =>
    fetchApi<AgentRun[]>(`/analysis/${jobId}/agents`),

  getAgentRun: (jobId: string, agentRunId: string) =>
    fetchApi<AgentRun>(`/analysis/${jobId}/agents/${agentRunId}`),

  getAgentMetadata: () =>
    fetchApi<AgentMetadata[]>('/analysis/agents/metadata'),

  // Diagrams
  getDiagrams: (projectId: string) =>
    fetchApi<Diagram[]>(`/projects/${projectId}/diagrams`),

  getDiagram: (id: string) => fetchApi<Diagram>(`/diagrams/${id}`),

  createDiagram: (data: { name: string; projectId: string }) =>
    fetchApi<Diagram>('/diagrams', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateDiagram: (id: string, data: UpdateDiagramInput) =>
    fetchApi<Diagram>(`/diagrams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteDiagram: (id: string, permanent = false) =>
    fetchApi<void>(`/diagrams/${id}${permanent ? '?permanent=true' : ''}`, { method: 'DELETE' }),

  // Diagram Trash & Versioning (Issue #158)
  getDeletedDiagrams: (projectId: string) =>
    fetchApi<Diagram[]>(`/diagrams/deleted/${projectId}`),

  restoreDiagram: (id: string) =>
    fetchApi<Diagram>(`/diagrams/${id}/restore`, { method: 'POST' }),

  getDiagramVersions: (id: string) =>
    fetchApi<Diagram[]>(`/diagrams/${id}/versions`),

  updateDiagramWithVersion: (id: string, data: UpdateDiagramInput, createVersion = false) =>
    fetchApi<Diagram>(`/diagrams/${id}${createVersion ? '?createVersion=true' : ''}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Regenerate diagram (Issue #239)
  regenerateDiagram: (id: string) =>
    fetchApi<{ jobId: string; diagramType: string; message: string }>(`/diagrams/${id}/regenerate`, {
      method: 'POST',
    }),

  // api.exportDiagram removed in #1074 B5 — diagram export happens client-side
  // via apps/web/src/components/diagram/renderers/shared/exportUtils.ts.

  // Detection Settings (Issue #381)
  getDetectionPatterns: (projectId: string, category?: DetectionPatternCategory) =>
    fetchApi<DetectionPatternsResponse>(
      `/projects/${projectId}/detection-patterns${category ? `?category=${category}` : ''}`
    ),

  getDetectionCategories: (projectId: string) =>
    fetchApi<{ categories: CategoryInfo[] }>(`/projects/${projectId}/detection-patterns/categories`),

  getDetectionPattern: (projectId: string, patternKey: string) =>
    fetchApi<EffectivePattern>(`/projects/${projectId}/detection-patterns/${patternKey}`),

  updateDetectionPattern: (projectId: string, patternKey: string, data: UpdatePatternOverrideInput) =>
    fetchApi<PatternUpdateResponse>(`/projects/${projectId}/detection-patterns/${patternKey}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  resetDetectionPattern: (projectId: string, patternKey: string) =>
    fetchApi<{ success: boolean; message: string; defaultRegex: string }>(`/projects/${projectId}/detection-patterns/${patternKey}`, {
      method: 'DELETE',
    }),

  resetAllDetectionPatterns: (projectId: string) =>
    fetchApi<{ success: boolean; message: string; deletedCount: number }>(`/projects/${projectId}/detection-patterns`, {
      method: 'DELETE',
    }),

  testDetectionPattern: (projectId: string, data: TestPatternRequest) =>
    fetchApi<TestPatternResult>(`/projects/${projectId}/detection-patterns/test`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  bulkUpdateDetectionPatterns: (
    projectId: string,
    updates: Array<{ patternKey: string } & UpdatePatternOverrideInput>
  ) =>
    fetchApi<{
      success: boolean;
      results: Array<{ patternKey: string; success: boolean; error?: string }>;
      summary: { total: number; succeeded: number; failed: number };
    }>(`/projects/${projectId}/detection-patterns/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    }),

  // Per-Interaction Prompt Override (Issue #386)
  getInteractionPromptOverride: (projectId: string, interactionId: string) =>
    fetchApi<{
      hasOverride: boolean;
      analysisDepth?: string;
      customGuidelines?: string | null;
      customSystemPrompt?: string | null;
      updatedAt?: string;
    }>(`/projects/${projectId}/interactions/${interactionId}/prompt-override`),

  saveInteractionPromptOverride: (
    projectId: string,
    interactionId: string,
    data: {
      analysisDepth?: 'light' | 'standard' | 'deep';
      customGuidelines?: string | null;
      customSystemPrompt?: string | null;
    }
  ) =>
    fetchApi<{
      success: boolean;
      analysisDepth: string;
      customGuidelines: string | null;
      customSystemPrompt: string | null;
      updatedAt: string;
    }>(`/projects/${projectId}/interactions/${interactionId}/prompt-override`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteInteractionPromptOverride: (projectId: string, interactionId: string) =>
    fetchApi<{ success: boolean }>(`/projects/${projectId}/interactions/${interactionId}/prompt-override`, {
      method: 'DELETE',
    }),

  getAllInteractionPromptOverrides: (projectId: string) =>
    fetchApi<{
      overrides: Record<string, {
        analysisDepth: string;
        hasCustomGuidelines: boolean;
        hasCustomSystemPrompt: boolean;
      }>;
    }>(`/projects/${projectId}/interactions/prompt-overrides`),

  // Issue #389: Update E2E flow layout (node positions)
  updateE2EFlowLayout: (
    projectId: string,
    diagramId: string,
    nodePositions: Record<string, { x: number; y: number }>,
    viewport?: { zoom: number; x: number; y: number }
  ) =>
    fetchApi<{
      success: boolean;
      diagramId: string;
      updatedNodes: number;
      viewportUpdated: boolean;
    }>(`/projects/${projectId}/e2e-flows/layout`, {
      method: 'PUT',
      body: JSON.stringify({ diagramId, nodePositions, viewport }),
    }),

  // Issue #392: E2E Flow AI Enhancement
  // Issue #407: Added evidenceLevel and evidenceSources
  // Issue #411: Added enableVerification option and verification result fields
  // Issue #417: Added customGuidelines, excludeFiles, and additionalFiles for prompt customization
  enhanceE2EFlow: (
    projectId: string,
    flowId: string,
    options: {
      depth?: 'standard' | 'deep' | 'ultra-deep';
      customGuidelines?: string;
      /** Issue #411: Enable AI verification loop for better accuracy */
      enableVerification?: boolean;
      /** Issue #417: Files to exclude from context */
      excludeFiles?: string[];
      /** Issue #417: Additional files to include in context */
      additionalFiles?: Array<{ filePath: string; content: string; size: number }>;
    } = {}
  ) =>
    fetchApi<{
      flowId: string;
      depth: 'standard' | 'deep' | 'ultra-deep';
      suggestedName?: string;
      originalName?: string;
      suggestedDescription?: string;
      originalDescription?: string;
      stepEnhancements: Array<{
        stepId: string;
        originalLabel?: string;
        suggestedLabel: string;
        originalDescription?: string;
        suggestedDescription?: string;
        reasoning: string;
        confidence: number;
        evidenceLevel?: 'strong' | 'partial' | 'inferred';
        evidenceSources?: Array<{
          type: 'file' | 'snippet' | 'pattern' | 'ast';
          filePath?: string;
          lineStart?: number;
          lineEnd?: number;
          description: string;
        }>;
      }>;
      missingSteps: Array<{
        afterStepId: string;
        suggestedType: string;
        suggestedName: string;
        description: string;
        reasoning: string;
        confidence: number;
        evidenceLevel?: 'strong' | 'partial' | 'inferred';
        evidenceSources?: Array<{
          type: 'file' | 'snippet' | 'pattern' | 'ast';
          filePath?: string;
          lineStart?: number;
          lineEnd?: number;
          description: string;
        }>;
        /** Issue #436: Category distinguishing missed vs suggested steps */
        category?: 'missed' | 'suggested';
        /** Issue #436: Code reference for missed steps */
        codeReference?: {
          functionName?: string;
          variableName?: string;
          apiEndpoint?: string;
          codeSnippet?: string;
        };
      }>;
      completeness: {
        score: number;
        reasoning: string;
        suggestions: string[];
        gaps?: string[];
      };
      enhancements: Array<{
        type: 'name' | 'description' | 'stepLabel' | 'missingStep' | 'completeness';
        stepId?: string;
        original?: string;
        suggested: string;
        reasoning: string;
        confidence: number;
        accepted?: boolean;
        evidenceLevel?: 'strong' | 'partial' | 'inferred';
        evidenceSources?: Array<{
          type: 'file' | 'snippet' | 'pattern' | 'ast';
          filePath?: string;
          lineStart?: number;
          lineEnd?: number;
          description: string;
        }>;
      }>;
      tokensUsed?: number;
      durationMs?: number;
      cached?: boolean;
      /** Issue #411: Whether AI verification loop was used */
      verificationUsed?: boolean;
      /** Issue #411: Number of verification questions asked */
      questionsAsked?: number;
      /** Issue #411: Details of verification questions and answers */
      verificationDetails?: Array<{
        question: string;
        questionType: string;
        answer: string;
        confidence: number;
      }>;
      /** Issue #411: What changed between Phase 1 (initial) and Phase 2 (verified) */
      verificationRevisions?: Array<{
        type: 'name' | 'description' | 'missingStepRemoved' | 'missingStepAdded' | 'stepLabelChanged';
        original?: string;
        revised?: string;
        reason: string;
      }>;
      /** Issue #424: Full prompt sent to AI */
      promptSent?: string;
      /** Issue #424: Raw AI response */
      aiResponse?: string;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/enhance`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  // Issue #417: Preview the AI prompt without executing
  previewE2EFlowEnhancement: (
    projectId: string,
    flowId: string,
    options: {
      depth?: 'standard' | 'deep' | 'ultra-deep';
      customGuidelines?: string;
      enableVerification?: boolean;
      excludeFiles?: string[];
    } = {}
  ) =>
    fetchApi<{
      systemPrompt: string;
      guidelines: string;
      customGuidelines?: string;
      flowContext: string;
      codeContext: {
        apiCalls: string;
        databaseOps: string;
        evidenceSnippets: string;
        fileContents: Array<{
          filePath: string;
          content: string;
          relevance: string;
          size: number;
          included: boolean;
        }>;
      };
      outputInstructions: string;
      verificationPrompt?: string;
      fullPrompt: string;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/enhance/preview`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  // Issue #417: Load file content for additional context
  loadFileContent: (projectId: string, filePath: string) =>
    fetchApi<{
      filePath: string;
      content: string;
      size: number;
      error?: string;
    }>(`/projects/${projectId}/files/content`, {
      method: 'POST',
      body: JSON.stringify({ filePath }),
    }),

  applyE2EFlowEnhancements: (
    projectId: string,
    flowId: string,
    data: {
      flowName?: string;
      flowDescription?: string;
      stepEnhancements?: Array<{
        stepId: string;
        suggestedLabel: string;
        suggestedDescription?: string;
      }>;
      missingSteps?: Array<{
        afterStepId: string;
        suggestedType: string;
        suggestedName: string;
        description?: string;
      }>;
    }
  ) =>
    fetchApi<{
      success: boolean;
      flowId: string;
      updatedFlow: unknown;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/apply-enhancements`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Issue #424: Added includeDetails param to optionally fetch promptSent/aiResponse
  // Issue #437: Added missingSteps and stepEnhancements fields
  getE2EFlowEnhancementHistory: (projectId: string, flowId: string, includeDetails = false) =>
    fetchApi<Array<{
      id: string;
      projectId: string;
      flowId: string;
      enhancementDepth: string;
      originalFlow: unknown;
      enhancedFlow?: unknown;
      enhancements: unknown[];
      // Issue #437: Full missing steps and step enhancements with categories
      missingSteps?: Array<{
        afterStepId: string;
        suggestedType: string;
        suggestedName: string;
        description: string;
        reasoning: string;
        confidence: number;
        evidenceLevel?: string;
        evidenceSources?: Array<{
          type: string;
          filePath?: string;
          lineStart?: number;
          lineEnd?: number;
          description: string;
        }>;
        category?: 'missed' | 'suggested';
        codeReference?: {
          functionName?: string;
          variableName?: string;
          apiEndpoint?: string;
          codeSnippet?: string;
        };
      }>;
      stepEnhancements?: Array<{
        stepId: string;
        originalLabel?: string;
        suggestedLabel: string;
        originalDescription?: string;
        suggestedDescription?: string;
        reasoning: string;
        confidence: number;
        evidenceLevel?: string;
        evidenceSources?: Array<{
          type: string;
          filePath?: string;
          lineStart?: number;
          lineEnd?: number;
          description: string;
        }>;
      }>;
      completenessScore?: number;
      tokensUsed?: number;
      applied: boolean;
      promptSent?: string;    // Issue #424
      aiResponse?: string;    // Issue #424
      createdAt: string;
      updatedAt: string;
    }>>(`/projects/${projectId}/e2e-flows/${flowId}/enhancement-history${includeDetails ? '?includeDetails=true' : ''}`),

  // Issue #437: Get enhancement summary for all flows (for catalog badges)
  getE2EFlowEnhancementSummary: (projectId: string) =>
    fetchApi<{
      flows: Record<string, {
        hasEnhancements: boolean;
        applied: boolean;
        missedCount: number;
        suggestedCount: number;
        totalCount: number;
        latestEnhancementDate: string;
      }>;
    }>(`/projects/${projectId}/e2e-flows/enhancement-summary`),

  // Issue #415: E2E Flow Version Management
  getE2EFlowVersions: (projectId: string, flowId: string) =>
    fetchApi<{
      flowId: string;
      diagramId: string;
      versions: Array<{
        id: string;
        flowId: string;
        version: number;
        isLatest: boolean;
        source: string;
        createdAt: string;
        flowName: string;
        stepCount: number;
        isComplete: boolean;
        // Issue #425: Enhancement details for AI-enhanced versions
        enhancement?: {
          id: string;
          depth: string;
          promptSent?: string;
          aiResponse?: string;
          tokensUsed?: number;
          completenessScore?: number;
        };
      }>;
      totalVersions: number;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/versions`),

  getE2EFlowVersion: (projectId: string, flowId: string, versionId: string) =>
    fetchApi<{
      id: string;
      flowId: string;
      diagramId: string;
      version: number;
      isLatest: boolean;
      source: string;
      flowData: unknown;
      createdAt: string;
      updatedAt: string;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/versions/${versionId}`),

  regenerateE2EFlow: (projectId: string, flowId: string) =>
    fetchApi<{
      success: boolean;
      flowId: string;
      newVersionId: string;
      version: number;
      previousVersionId: string | null;
      message: string;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/regenerate`, {
      method: 'POST',
    }),

  revertE2EFlowVersion: (projectId: string, flowId: string, versionId: string) =>
    fetchApi<{
      success: boolean;
      flowId: string;
      revertedToVersion: number;
      newVersion: {
        id: string;
        version: number;
        source: string;
      };
      flowData: unknown;
    }>(`/projects/${projectId}/e2e-flows/${flowId}/versions/${versionId}/revert`, {
      method: 'POST',
    }),

  exportE2EFlow: async (
    projectId: string,
    flowId: string,
    options: {
      format: 'png' | 'svg' | 'json';
      width?: number;
      height?: number;
      backgroundColor?: string;
      includeMetadata?: boolean;
    }
  ): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/projects/${projectId}/e2e-flows/${flowId}/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  },

  // Issue #460: Get import dependency graph for a project
  getImportGraph: (
    projectId: string,
    options: { directory?: string; includeEvidence?: boolean; refresh?: boolean } = {}
  ) => {
    const params = new URLSearchParams();
    if (options.directory) params.set('directory', options.directory);
    if (options.includeEvidence) params.set('includeEvidence', 'true');
    if (options.refresh) params.set('refresh', 'true');
    const queryString = params.toString();
    return fetchApi<{
      projectId: string;
      projectName: string;
      graph: {
        nodes: Array<{
          id: string;
          name: string;
          filePath: string;
          role: 'controller' | 'service' | 'repository' | 'middleware' | 'utility' | 'model' | 'unknown';
          layer: number;
          exportCount: number;
          importCount: number;
          importedBy: string[];
          imports: string[];
          isBarrel: boolean;
          hasCircularDep: boolean;
          isEntryPoint: boolean;
        }>;
        edges: Array<{
          id: string;
          source: string;
          target: string;
          type: 'import' | 'reexport' | 'typeOnly';
          imports: string[];
          isCircular: boolean;
        }>;
        circularDependencies: string[][];
        layers: {
          controllers: string[];
          services: string[];
          repositories: string[];
          middleware: string[];
          utilities: string[];
          models: string[];
          unknown: string[];
        };
        barrels: string[];
      };
      metadata: {
        totalFiles: number;
        totalImports: number;
        totalExports: number;
        circularDependencyCount: number;
        entryPointCount: number;
        barrelFileCount: number;
      };
      generatedAt: string;
      cached?: boolean;
      cachedAt?: string;
    }>(`/projects/${projectId}/import-graph${queryString ? `?${queryString}` : ''}`);
  },

  // Issue #501: Scan files before generation (fast, no AST)
  scanImportGraphFiles: (projectId: string) =>
    fetchApi<{
      projectId: string;
      totalFiles: number;
      directories: Array<{ path: string; fileCount: number }>;
      activeExcludePatterns: string[];
    }>(`/projects/${projectId}/import-graph/scan`),

  // Issue #501: Start async import graph generation job
  generateImportGraph: (projectId: string) =>
    fetchApi<{ jobId: string }>(`/projects/${projectId}/import-graph/generate`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Issue #501: Poll job status
  getImportGraphJobStatus: (projectId: string, jobId: string) =>
    fetchApi<{
      status: 'pending' | 'running' | 'complete' | 'error';
      progress: number;
      message: string;
      filesCompleted: number;
      totalFiles: number;
      result: object | null;
      error: string | null;
    }>(`/projects/${projectId}/import-graph/status/${jobId}`),

  // #1045: Generated-diagrams API client methods (#466) removed —
  // their backend endpoints (/diagrams/generate-from-analysis,
  // /diagrams/generated/project/:id, /diagrams/generated/:id) were
  // dropped in #666 along with the GeneratedDiagram model. The SPA
  // had been calling these into a 404 since then.

  // Issue #562: Saved graph filter views
  getSavedGraphViews: (projectId: string, graphType?: string) =>
    fetchApi<SavedGraphView[]>(
      `/projects/${projectId}/saved-views${graphType ? `?graphType=${encodeURIComponent(graphType)}` : ''}`
    ),

  createSavedGraphView: (projectId: string, payload: { name: string; graphType: string; filters: GraphFilterState }) =>
    fetchApi<SavedGraphView>(`/projects/${projectId}/saved-views`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateSavedGraphView: (projectId: string, viewId: string, payload: { name?: string; filters?: GraphFilterState }) =>
    fetchApi<SavedGraphView>(`/projects/${projectId}/saved-views/${viewId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteSavedGraphView: (projectId: string, viewId: string) =>
    fetchApi<void>(`/projects/${projectId}/saved-views/${viewId}`, { method: 'DELETE' }),

  // Issue #864 Phase B.5: Stitch-resolved caller<->endpoint mappings.
  getStitchedMappings: (projectId: string) =>
    fetchApi<{
      callerToEndpoint: Record<string, { routeId: string; method: string; path: string; framework: string; confidence: number; source: string }>;
      endpointToCallers: Record<string, Array<{ callSiteId: string; filePath: string; method: string; urlPattern: string; enclosingSymbol: string; source: string }>>;
    }>(`/projects/${projectId}/stitched-mappings`),

  // Issue #818 (epic #802): Stitch review endpoints — FCG/BCG auto-stitcher
  // pipeline introduced in #810–#814.
  getStitchReview: (projectId: string) =>
    fetchApi<StitchReviewResponse>(`/projects/${projectId}/stitches/review`),

  // Issue #963 track 3: click-stitch review — UI handler → api-call layer.
  getClickStitchReview: (projectId: string, opts?: { offset?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.offset != null) params.set('offset', String(opts.offset));
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return fetchApi<{
      unstitched: Array<{
        handler: { handlerFile: string; handlerFunction: string; triggerElement: string; eventType: string; component?: string };
        component?: string;
        triggerElement: string;
        eventType: string;
        handlerFile: string;
        handlerLine?: number;
        handlerFunction: string;
        calledFunctions: string[];
      }>;
      // #975: pagination — `totalUnstitched` is the true count
      // pre-slice; `unstitched` is the current page.
      totalUnstitched: number;
      unstitchedOffset: number;
      unstitchedLimit: number;
      autoStitches: Array<{ kind: 'click'; handler: Record<string, string>; apiCall: { method: string; endpoint: string; sourceFile: string; sourceContext?: string }; confidence: number; source: 'auto'; rationale: string; createdAt: string }>;
      userStitches: Array<{ kind: 'click'; handler: Record<string, string>; apiCall: { method: string; endpoint: string; sourceFile: string; sourceContext?: string }; confidence: number; source: 'user'; rationale: string; createdAt: string }>;
      aiStitches: Array<{ kind: 'click'; handler: Record<string, string>; apiCall: { method: string; endpoint: string; sourceFile: string; sourceContext?: string }; confidence: number; source: 'ai'; rationale: string; createdAt: string }>;
      // #971: handlers explicitly marked as UI-only.
      ignoredHandlers: Array<{ handlerFile: string; handlerFunction: string; triggerElement: string; eventType: string; component?: string }>;
      // #979: heuristic auto-ignored handlers (e.g. onClose, e.stopPropagation).
      // Computed each request — not persisted.
      autoIgnoredHandlers: Array<{
        handler: { handlerFile: string; handlerFunction: string; triggerElement: string; eventType: string; component?: string };
        reason: string;
      }>;
      // #977: provenance metadata — null when state is 'no-analysis'.
      generatedFrom: {
        analysisCompletedAt: string;
        stitchSetGeneratedAt: string | null;
      } | null;
      state: 'ready' | 'no-analysis';
    }>(`/projects/${projectId}/stitches/click/review${qs ? `?${qs}` : ''}`);
  },

  // Issue #971: mark/unmark a handler as UI-only.
  ignoreClickHandler: (projectId: string, handler: Record<string, string>) =>
    fetchApi<{ ok: boolean }>(`/projects/${projectId}/stitches/click/ignore`, {
      method: 'POST',
      body: JSON.stringify({ handler }),
    }),

  unignoreClickHandler: (projectId: string, handler: Record<string, string>) =>
    fetchApi<{ ok: boolean }>(`/projects/${projectId}/stitches/click/unignore`, {
      method: 'POST',
      body: JSON.stringify({ handler }),
    }),

  bindStitch: (
    projectId: string,
    callSite: Record<string, string>,
    route: Record<string, string>,
    note?: string,
  ) =>
    fetchApi<{ ok: boolean }>(`/projects/${projectId}/stitches/bind`, {
      method: 'POST',
      body: JSON.stringify({ callSite, route, note }),
    }),

  ignoreStitch: (projectId: string, callSite: Record<string, string>) =>
    fetchApi<{ ok: boolean }>(`/projects/${projectId}/stitches/ignore`, {
      method: 'POST',
      body: JSON.stringify({ callSite }),
    }),

  createStitchRule: (
    projectId: string,
    rule: {
      match: { kind: string; pattern: string };
      target: { kind: string; pattern: string; method?: string };
      confidence?: number;
      note?: string;
    },
  ) =>
    fetchApi<{ ok: boolean; rule: { id: string } }>(
      `/projects/${projectId}/stitches/rules`,
      { method: 'POST', body: JSON.stringify(rule) },
    ),

  deleteStitchRule: (projectId: string, ruleId: string) =>
    fetchApi<{ ok: boolean }>(`/projects/${projectId}/stitches/rules/${ruleId}`, {
      method: 'DELETE',
    }),

  // Issue #829: cancel a running batch.
  cancelBatch: (projectId: string, batchId: string) =>
    fetchApi<{ ok: boolean }>(`/projects/${projectId}/stitches/propose/batch/cancel`, {
      method: 'POST',
      body: JSON.stringify({ batchId }),
    }),

  // Issue #827: unbind a user or AI stitch, returning the call site
  // to the unresolved pool.
  unbindStitch: (projectId: string, callSite: Record<string, string>) =>
    fetchApi<{ ok: boolean; removed: number }>(
      `/projects/${projectId}/stitches/unbind`,
      {
        method: 'POST',
        body: JSON.stringify({ callSite }),
      },
    ),

  proposeAIStitches: (
    projectId: string,
    opts: {
      maxProposals?: number;
      candidatesPerSite?: number;
      // Issue #818: when set, propose for just this one call site.
      callSiteId?: string;
    } = {},
  ) =>
    fetchApi<StitchProposeResult>(`/projects/${projectId}/stitches/propose`, {
      method: 'POST',
      body: JSON.stringify(opts),
    }),

  // Issue #818: preview the prompt we would send to Claude for a
  // single call site, without actually calling Claude. Mirrors the
  // E2E-flow enhance/preview pattern.
  previewStitchPrompt: (
    projectId: string,
    callSiteId: string,
    candidatesPerSite = 5,
  ) =>
    fetchApi<StitchPromptPreview>(
      `/projects/${projectId}/stitches/propose/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ callSiteId, candidatesPerSite }),
      },
    ),

  // Issue #1012: combined dashboard summary — returns structural +
  // operational + security in one response. Frontend dashboard should
  // prefer this over the three separate endpoints when it needs all
  // three (the common case). Saves 2 TCP/TLS round-trips and shares
  // one bundle load + one ETag check.
  getDashboardSummary: (opts: {
    projectId?: string;
    operationalWindow?: '1h' | '24h' | '7d' | '30d';
    securityWindow?: '7d' | '30d' | '90d' | 'all';
    refresh?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    if (opts.projectId) params.set('projectId', opts.projectId);
    if (opts.operationalWindow) params.set('operationalWindow', opts.operationalWindow);
    if (opts.securityWindow) params.set('securityWindow', opts.securityWindow);
    if (opts.refresh) params.set('refresh', '1');
    const qs = params.toString();
    return fetchApi<{
      structural: StructuralSummaryPayload;
      operational: OperationalSummaryPayload;
      security: SecuritySummaryPayload | null;
    }>(`/dashboard/summary${qs ? `?${qs}` : ''}`);
  },

  // Issue #819 Phase 1: Cross-project Structural Summary payload.
  getStructuralSummary: (opts: { projectId?: string; refresh?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (opts.projectId) params.set('projectId', opts.projectId);
    if (opts.refresh) params.set('refresh', '1');
    const qs = params.toString();
    return fetchApi<StructuralSummaryPayload>(
      `/dashboard/structural/summary${qs ? `?${qs}` : ''}`,
    );
  },

  // Issue #819 Phase 3: Security Summary payload.
  getSecuritySummary: (opts: {
    projectId?: string;
    window?: '7d' | '30d' | '90d' | 'all';
    refresh?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    if (opts.projectId) params.set('projectId', opts.projectId);
    if (opts.window) params.set('window', opts.window);
    if (opts.refresh) params.set('refresh', '1');
    const qs = params.toString();
    return fetchApi<SecuritySummaryPayload>(
      `/dashboard/security/summary${qs ? `?${qs}` : ''}`,
    );
  },

  // Issue #819 Phase 2: Operational Summary payload.
  getOperationalSummary: (opts: {
    projectId?: string;
    window?: '1h' | '24h' | '7d' | '30d';
    refresh?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    if (opts.projectId) params.set('projectId', opts.projectId);
    if (opts.window) params.set('window', opts.window);
    if (opts.refresh) params.set('refresh', '1');
    const qs = params.toString();
    return fetchApi<OperationalSummaryPayload>(
      `/dashboard/operational/summary${qs ? `?${qs}` : ''}`,
    );
  },

  getBackendCallGraph: (projectId: string) =>
    fetchApi<{
      backendCallGraph: {
        endpoints: Array<{
          routeId: string;
          routeKey: {
            framework: string;
            method: string;
            path: string;
            handlerSymbol: string;
            filePath: string;
          };
          method: string;
          path: string;
          framework: string;
          handlerFunction?: string;
        }>;
      };
      // Issue #910: per-source provenance, see getFrontendCallGraph.
      sources?: Array<{
        diagramId: string;
        sourceId: string | null;
        name: string;
      }>;
    }>(`/projects/${projectId}/backend-call-graph`),

  getFrontendCallGraph: (projectId: string) =>
    fetchApi<{
      frontendCallGraph: {
        projectId: string;
        version: number;
        generatedAt: string;
        nodes: Record<string, unknown>;
        edges: Array<unknown>;
        apiCallSites: Array<{
          callSiteId: string;
          callSiteKey: {
            filePath: string;
            enclosingSymbol: string;
            urlPattern: string;
            method: string;
            wrapperChainHash: string;
          };
          filePath: string;
          lineStart: number;
          lineEnd: number;
          urlPattern: string;
          method: string;
          library: string;
          wrapperChain: string[];
          isDynamicEndpoint: boolean;
        }>;
        stats: {
          uiElements: number;
          handlers: number;
          functions: number;
          hooks: number;
          callSites: number;
          dynamicCallSites: number;
        };
      };
      diagramId: string;
      // Issue #910: per-source provenance. For multi-source projects
      // the response carries the merged FCG and an array describing
      // each contributing source row. Single-source projects get
      // a one-element array.
      sources?: Array<{
        diagramId: string;
        sourceId: string | null;
        name: string;
      }>;
      updatedAt: string;
    }>(`/projects/${projectId}/frontend-call-graph`),

  // Issue #864 Phase A: regenerate E2E flows from current stitch state.
  regenerateFlowsFromStitches: (projectId: string) =>
    fetchApi<{ success: boolean; flowCount: number }>(
      `/projects/${projectId}/e2e-flows/regenerate-from-stitches`,
      { method: 'POST' },
    ),

  // Issue #863 (epic #831): canonical on-demand E2E flow generation.
  generateE2EFlows: (projectId: string) =>
    fetchApi<{
      flowCount: number;
      diagramId: string;
      generatedAt: string;
      durationMs: number;
      stitchSnapshot: {
        auto: number;
        user: number;
        ai: number;
        unresolved: number;
        ambiguous: number;
      };
      orphanedStitches: { user: number; ai: number; auto: number };
    }>(`/projects/${projectId}/e2e-flows/generate`, { method: 'POST' }),

  // Issue #863: getStitchedMappings already exists above (added in #864
  // work) — kept singular `callerToEndpoint` shape for compatibility
  // with FrontendGraphPage / Endpoints / Callers consumers.
};
